import { reactive } from "vue";
import {
    CallToActionType,
    ONBOARDING_STEPS,
    type OnboardingStep,
} from "../models/onboarding_steps.ts";
import {
    disableOnboarding,
    getOnboardingProgress,
    updateOnboardingStep,
    type OnboardingResponse,
    type OnboardingStepResponse,
} from "../services/onboarding.service.ts";

export interface OrderedStep extends OnboardingStep {
    step_number: number;
    status: "completed" | "skipped" | "pending" | null;
}

export const onboardingProgress = reactive({
    enableOnboarding: false,
    steps: [] as OnboardingStepResponse[],
    currentStepCode: null as string | null,
    isLoaded: false,
});

function getStepByCode(stepCode: string): OnboardingStep | undefined {
    return ONBOARDING_STEPS.find((s) => s.step_code === stepCode);
}

function syncFromApiResponse(response: OnboardingResponse) {
    onboardingProgress.enableOnboarding = response.is_onboarding_enabled;

    const sorted = [...response.steps].sort((a, b) => a.step_number - b.step_number);
    onboardingProgress.steps = sorted;

    const firstBlocking = sorted.find((s) => s.status === null || s.status === "pending");
    onboardingProgress.currentStepCode = firstBlocking
        ? firstBlocking.step_code
        : (sorted[sorted.length - 1]?.step_code ?? null);

    onboardingProgress.isLoaded = true;
}

export async function loadOnboardingProgressFromBackend() {
    const response = await getOnboardingProgress();
    syncFromApiResponse(response);
    return response;
}

export async function completeStep(stepCode: string) {
    const response = await updateOnboardingStep(stepCode, "completed");
    syncFromApiResponse(response);
}

export async function skipStep(stepCode: string) {
    const uiStep = getStepByCode(stepCode);
    if (!uiStep?.is_skippable) {
        return;
    }

    const response = await updateOnboardingStep(stepCode, "skipped");
    syncFromApiResponse(response);
}

export async function setPendingStep(stepCode: string) {
    const response = await updateOnboardingStep(stepCode, "pending");
    syncFromApiResponse(response);
}

export function setCurrentStep(stepCode: string) {
    const apiStep = onboardingProgress.steps.find((s) => s.step_code === stepCode);
    if (!apiStep) {
        return;
    }

    // Find the first blocking step (pending or unresolved)
    const firstBlocking = onboardingProgress.steps.find(
        (s) => s.status === null || s.status === "pending",
    );

    // Allow navigating up to and including the blocking step, but not past it
    if (!firstBlocking || apiStep.step_number <= firstBlocking.step_number) {
        onboardingProgress.currentStepCode = stepCode;
    }
}

export function getOrderedSteps(): OrderedStep[] {
    return onboardingProgress.steps
        .map((apiStep) => {
            const uiStep = getStepByCode(apiStep.step_code);
            if (!uiStep) {
                return null;
            }

            return {
                ...uiStep,
                step_number: apiStep.step_number,
                status: apiStep.status,
            };
        })
        .filter((s): s is OrderedStep => s !== null);
}

export function getCurrentStepRoutePath() {
    const uiStep = onboardingProgress.currentStepCode
        ? getStepByCode(onboardingProgress.currentStepCode)
        : undefined;

    if (!uiStep || uiStep.main_call_to_action_type !== CallToActionType.Path) {
        return null;
    }

    return new URL(uiStep.main_call_to_action_string as string, window.location.origin);
}

export async function finishOnboarding() {
    const response = await disableOnboarding();
    syncFromApiResponse(response);
}

export function isOnboardingInProgress() {
    if (!onboardingProgress.enableOnboarding) {
        return false;
    }

    return onboardingProgress.steps.some((s) => s.status === null || s.status === "pending");
}

export function isOnboardingEnabled() {
    return onboardingProgress.enableOnboarding;
}

/**
 * Steps displayed in the Onboarding.vue wizard view (excludes OPEN_ITEMS which is shown inline).
 */
export function getOnboardingViewSteps(): OrderedStep[] {
    return getOrderedSteps().filter((s) => s.step_code !== "OPEN_ITEMS");
}

export function getOpenItemsStep(): OrderedStep | undefined {
    return getOrderedSteps().find((s) => s.step_code === "OPEN_ITEMS");
}

export function isCurrentStepOpenItems(): boolean {
    return onboardingProgress.currentStepCode === "OPEN_ITEMS";
}
